import type { CurrentPlan } from "@circle-react/types";
import type { AuxiliarySubscriptionResponse } from "@circle-react/types/UsageBilling";
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import { isPlanPlus } from "@/react/helpers/planHelpers";

import { useAuxiliarySubscriptionApi } from "../../hooks/useAuxiliarySubscriptionApi";
import { useBillingDetailsApi } from "../../hooks/useBillingDetailsApi";

interface BillingContextValue {
  auxiliarySubscription?: AuxiliarySubscriptionResponse;
  currentPlan?: CurrentPlan;
  errorFetchingAuxiliarySubscription: unknown;
  errorFetchingCurrentPlan: unknown;
  isAuxiliarySubscriptionLoading: boolean;
  isCurrentPlanLoading: boolean;
  isCurrentPlanPlus: boolean;
  refetchAuxiliarySubscription: () => unknown;
  refetchCurrentPlan: () => unknown;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

const BillingContext = createContext<BillingContextValue>({
  auxiliarySubscription: undefined,
  currentPlan: undefined,
  errorFetchingAuxiliarySubscription: null,
  errorFetchingCurrentPlan: null,
  isAuxiliarySubscriptionLoading: false,
  isCurrentPlanLoading: false,
  isCurrentPlanPlus: false,
  refetchAuxiliarySubscription: () => {},
  refetchCurrentPlan: () => {},
  showAddons: false,
  showBillingDetails: false,
  showBillingPortal: false,
});

BillingContext.displayName = "BillingContext";

interface BillingProviderProps {
  children: ReactNode;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingProvider = ({
  showAddons,
  showBillingDetails,
  showBillingPortal,
  children,
}: BillingProviderProps) => {
  const {
    currentPlan,
    isCurrentPlanLoading,
    errorFetchingCurrentPlan,
    refetchCurrentPlan,
  } = useBillingDetailsApi();

  const {
    auxiliarySubscription,
    isAuxiliarySubscriptionLoading,
    errorFetchingAuxiliarySubscription,
    refetchAuxiliarySubscription,
  } = useAuxiliarySubscriptionApi();

  const value = useMemo(
    () => ({
      auxiliarySubscription,
      currentPlan,
      errorFetchingAuxiliarySubscription,
      errorFetchingCurrentPlan,
      isAuxiliarySubscriptionLoading,
      isCurrentPlanLoading,
      isCurrentPlanPlus: isPlanPlus(currentPlan?.tier),
      refetchAuxiliarySubscription,
      refetchCurrentPlan,
      showAddons,
      showBillingDetails,
      showBillingPortal,
    }),
    [
      auxiliarySubscription,
      currentPlan,
      errorFetchingAuxiliarySubscription,
      errorFetchingCurrentPlan,
      isAuxiliarySubscriptionLoading,
      isCurrentPlanLoading,
      refetchAuxiliarySubscription,
      refetchCurrentPlan,
      showAddons,
      showBillingDetails,
      showBillingPortal,
    ],
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
};

export const useBillingContext = () => useContext(BillingContext);
